import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, Users, ChefHat, ArrowLeft, Check } from 'lucide-react';
import { getRecipeBySlug, Recipe } from './recipeData';
import './RecipeDetail.css';

const RecipeDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const recipe = slug ? getRecipeBySlug(slug) : undefined;
  const [checkedIngredients, setCheckedIngredients] = useState<boolean[]>([]);

  useEffect(() => {
    if (recipe) {
      document.title = `${recipe.name} - HomeCookt`;
      setCheckedIngredients(new Array(recipe.ingredients.length).fill(false));
    } else {
      document.title = 'Recipe Not Found - HomeCookt';
    }
  }, [recipe]);

  if (!recipe) {
    return (
      <div className="recipe-detail-page">
        <div className="recipe-detail-container">
          <div className="recipe-not-found">
            <h1>Recipe Not Found</h1>
            <p>Sorry, we could not find that recipe.</p>
            <Link to="/recipes" className="recipe-back-link">
              <ArrowLeft size={18} />
              Back to all recipes
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const toggleIngredient = (index: number) => {
    setCheckedIngredients((prev) => prev.map((v, i) => (i === index ? !v : v)));
  };

  const buildSchema = (r: Recipe) => {
    const toIso = (minutes: string) => {
      const mins = parseInt(minutes, 10) || 0;
      return `PT${mins}M`;
    };

    const prepMins = parseInt(r.prepTime, 10) || 0;
    const cookMins = parseInt(r.cookTime, 10) || 0;

    return {
      '@context': 'https://schema.org',
      '@type': 'Recipe',
      name: r.name,
      description: r.description,
      recipeCuisine: r.cuisine,
      prepTime: toIso(r.prepTime),
      cookTime: toIso(r.cookTime),
      totalTime: toIso(String(prepMins + cookMins)),
      recipeYield: `${r.servings} servings`,
      recipeIngredient: r.ingredients,
      recipeInstructions: r.steps.map((step, i) => ({
        '@type': 'HowToStep',
        name: step.title,
        text: step.instruction,
        position: i + 1,
      })),
      keywords: r.keywords.join(', '),
      recipeCategory: 'Main course',
      recipeDifficulty: r.difficulty,
    };
  };

  return (
    <div className="recipe-detail-page">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildSchema(recipe)) }}
      />

      <div className="recipe-detail-container">
        <Link to="/recipes" className="recipe-back-link">
          <ArrowLeft size={18} />
          Back to all recipes
        </Link>

        <article className="recipe-detail-content">
          <header className="recipe-detail-header">
            <span className="recipe-detail-flag">{recipe.flag}</span>
            <h1 className="recipe-detail-title">{recipe.name}</h1>
            <p className="recipe-detail-cuisine">{recipe.cuisine}</p>
            <p className="recipe-detail-description">{recipe.description}</p>

            <div className="recipe-detail-meta">
              <div className="recipe-detail-meta-item">
                <Clock size={18} />
                <div className="recipe-detail-meta-text">
                  <span className="meta-label">Total Time</span>
                  <span className="meta-value">{recipe.totalTime}</span>
                </div>
              </div>
              <div className="recipe-detail-meta-item">
                <Users size={18} />
                <div className="recipe-detail-meta-text">
                  <span className="meta-label">Serves</span>
                  <span className="meta-value">{recipe.servings}</span>
                </div>
              </div>
              <div className="recipe-detail-meta-item">
                <ChefHat size={18} />
                <div className="recipe-detail-meta-text">
                  <span className="meta-label">Difficulty</span>
                  <span className="meta-value">{recipe.difficulty}</span>
                </div>
              </div>
            </div>

            <div className="recipe-detail-times">
              <span>Prep: {recipe.prepTime}</span>
              <span>Cook: {recipe.cookTime}</span>
            </div>
          </header>

          <section className="recipe-detail-section">
            <h2 className="recipe-section-title">Ingredients</h2>
            <p className="recipe-section-hint">Tap each ingredient to check it off as you go.</p>
            <ul className="recipe-ingredients-list">
              {recipe.ingredients.map((ingredient, index) => (
                <li
                  key={index}
                  className={`recipe-ingredient-item ${checkedIngredients[index] ? 'checked' : ''}`}
                  onClick={() => toggleIngredient(index)}
                >
                  <span className="recipe-ingredient-checkbox">
                    {checkedIngredients[index] && <Check size={14} />}
                  </span>
                  <span className="recipe-ingredient-text">{ingredient}</span>
                </li>
              ))}
            </ul>
          </section>

          <section className="recipe-detail-section">
            <h2 className="recipe-section-title">Step-by-Step</h2>
            <ol className="recipe-steps-list">
              {recipe.steps.map((step, index) => (
                <li key={index} className="recipe-step-item">
                  <span className="recipe-step-number">{index + 1}</span>
                  <div className="recipe-step-body">
                    <h3 className="recipe-step-title">{step.title}</h3>
                    <p className="recipe-step-instruction">{step.instruction}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {recipe.servingSuggestions && recipe.servingSuggestions.length > 0 && (
            <section className="recipe-detail-section">
              <h2 className="recipe-section-title">Serving Suggestions</h2>
              <ul className="recipe-tips-list">
                {recipe.servingSuggestions.map((tip, index) => (
                  <li key={index} className="recipe-tip-item">{tip}</li>
                ))}
              </ul>
            </section>
          )}

          {recipe.cookingTips && recipe.cookingTips.length > 0 && (
            <section className="recipe-detail-section">
              <h2 className="recipe-section-title">Cooking Tips</h2>
              <ul className="recipe-tips-list">
                {recipe.cookingTips.map((tip, index) => (
                  <li key={index} className="recipe-tip-item">{tip}</li>
                ))}
              </ul>
            </section>
          )}
        </article>

        <Link to="/recipes" className="recipe-back-link recipe-back-link-bottom">
          <ArrowLeft size={18} />
          Back to all recipes
        </Link>
      </div>
    </div>
  );
};

export default RecipeDetail;
